/**
 * Drawing transform helpers — bbox math + canonicalization.
 *
 * Two storage shapes flow through here:
 *
 *   Canonical (new drawings):
 *     - stroke points are normalized [0, 1] inside the drawing's own
 *       tight bounding box
 *     - `content.transform.{width, height}` = bbox size as a fraction
 *       of the host (panel)
 *     - `anchor.panel_point.{x, y}` = bbox center in panel-normalized
 *       coords
 *     - `content.transform.rotation` = degrees clockwise
 *
 *   Legacy (drawings created before the transform feature):
 *     - stroke points are normalized to the full panel
 *     - `content.transform = { width: 1, height: 1, rotation: 0 }`
 *     - `anchor` is absent
 *     - functionally equivalent to "the bbox covers the panel and the
 *       strokes paint inside it"
 *
 * `resolveDrawingFrame` papers over the two shapes so the renderer
 * (and the gesture/selection layers) only ever sees one canonical
 * frame: a bbox center (x, y), bbox size (width, height) — both in
 * panel-normalized units — and a rotation. For legacy rows the bbox
 * is computed lazily from the stroke point min/max so the selection
 * handles wrap the actual ink rather than the whole panel.
 *
 * On the first transform of a legacy drawing the consumer should
 * canonicalize via `canonicalizeLegacyDrawing` and PATCH once;
 * subsequent updates are pure transform/anchor patches.
 */
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
// Floor on a drawing's bbox dimension. A pure horizontal stroke has
// height ≈ 0 (every point shares y), which would make bbox-local
// normalization divide by zero. Same for a pure vertical stroke or
// a single-point tap. Falling back to a small but visible dimension
// keeps the math stable and gives the user a finite-area selection
// target. 0.001 is well below the perfect-freehand stroke width
// (renders ~10px at full panel) so it's invisible in practice.
const MIN_BBOX_DIM = 0.001;
// ---------------------------------------------------------------------------
// Bbox computation
// ---------------------------------------------------------------------------
/**
 * Tight bounding box of every point in every stroke. Returns
 * `undefined` only for the degenerate case of zero strokes — every
 * legitimate stroke has at least one point.
 */
export function computeStrokeBbox(strokes) {
    let xMin = Infinity;
    let yMin = Infinity;
    let xMax = -Infinity;
    let yMax = -Infinity;
    let hasPoint = false;
    for (const stroke of strokes) {
        for (const p of stroke.points) {
            hasPoint = true;
            if (p.x < xMin)
                xMin = p.x;
            if (p.x > xMax)
                xMax = p.x;
            if (p.y < yMin)
                yMin = p.y;
            if (p.y > yMax)
                yMax = p.y;
        }
    }
    if (!hasPoint)
        return undefined;
    return { xMin, yMin, xMax, yMax };
}
// ---------------------------------------------------------------------------
// Canonicalization
// ---------------------------------------------------------------------------
/**
 * Canonicalize a freshly-captured set of strokes (panel-normalized
 * coords) into the canonical bbox-local form ready for
 * `userAnnotationStore.create`.
 *
 * If the strokes are degenerate (no points, or all coincident), the
 * bbox falls back to a sub-pixel square at the geometric centroid so
 * the resulting row still validates against the schema and renders
 * as a tiny dot rather than throwing.
 */
export function canonicalizeFreshDrawing(strokes) {
    const bbox = computeStrokeBbox(strokes) ?? {
        xMin: 0.5,
        yMin: 0.5,
        xMax: 0.5,
        yMax: 0.5,
    };
    return canonicalizeFromBbox(strokes, bbox, 0);
}
/**
 * Canonicalize a legacy drawing on first transform. Reads the
 * existing strokes (panel-normalized) plus the legacy transform's
 * rotation, computes the tight bbox, and returns canonical strokes +
 * transform + anchor that render identically.
 *
 * Preserves the legacy rotation so a transform PATCH that only
 * rotates a never-touched legacy drawing doesn't silently reset its
 * orientation.
 */
export function canonicalizeLegacyDrawing(strokes, legacyRotation) {
    const bbox = computeStrokeBbox(strokes) ?? {
        xMin: 0.5,
        yMin: 0.5,
        xMax: 0.5,
        yMax: 0.5,
    };
    return canonicalizeFromBbox(strokes, bbox, legacyRotation);
}
function canonicalizeFromBbox(strokes, bbox, rotation) {
    const rawW = bbox.xMax - bbox.xMin;
    const rawH = bbox.yMax - bbox.yMin;
    const width = rawW < MIN_BBOX_DIM ? MIN_BBOX_DIM : rawW;
    const height = rawH < MIN_BBOX_DIM ? MIN_BBOX_DIM : rawH;
    const cx = (bbox.xMin + bbox.xMax) / 2;
    const cy = (bbox.yMin + bbox.yMax) / 2;
    const localStrokes = strokes.map((stroke) => ({
        points: stroke.points.map((p) => normalizePoint(p, bbox.xMin, bbox.yMin, width, height)),
    }));
    // Natural width/height equal the canonicalized width/height at
    // creation time. They never update on a resize gesture; renderers
    // rely on that to keep stroke weight scaling proportionally with
    // the bbox.
    return {
        strokes: localStrokes,
        transform: {
            width,
            height,
            rotation,
            naturalWidth: width,
            naturalHeight: height,
        },
        anchor: { type: "panel_point", x: cx, y: cy },
    };
}
function normalizePoint(p, xMin, yMin, width, height) {
    const out = {
        x: (p.x - xMin) / width,
        y: (p.y - yMin) / height,
    };
    if (p.pressure !== undefined)
        out.pressure = p.pressure;
    return out;
}
// ---------------------------------------------------------------------------
// Frame resolver
// ---------------------------------------------------------------------------
/**
 * Resolve a drawing annotation (legacy or canonical) into the
 * canonical frame the renderer expects. The returned `strokes` are
 * always bbox-local [0, 1], so the renderer can size the SVG to the
 * bbox rect and paint stroke coords directly.
 *
 * For legacy rows this is a per-render computation (cheap — bbox is
 * one pass over the points; stroke normalization is one allocation
 * per stroke). The caller is expected to memoize on
 * `annotation.id`/`annotation.updatedAt` if it cares.
 */
export function resolveDrawingFrame(annotation) {
    const t = annotation.content.transform;
    const anchor = annotation.anchor?.type === "panel_point" ? annotation.anchor : undefined;
    if (anchor !== undefined) {
        // Canonical row: anchor + transform define the frame and stroke
        // coords are already bbox-local. Natural width/height fall back
        // to current width/height for canonical rows that pre-date the
        // natural-size fields (an early-canonical row was committed
        // before outlined-stroke shipped); the first transform PATCH
        // after the upgrade will populate them.
        return {
            x: anchor.x,
            y: anchor.y,
            width: t.width,
            height: t.height,
            rotation: t.rotation,
            naturalWidth: t.naturalWidth ?? t.width,
            naturalHeight: t.naturalHeight ?? t.height,
            strokes: annotation.content.strokes,
        };
    }
    // Legacy row: strokes are panel-normalized, no anchor. Compute the
    // tight bbox from the points and rebase strokes into it so the
    // renderer can treat the legacy case identically to canonical.
    // Natural width/height for a legacy row are the bbox at first
    // resolve — i.e. the size the row has always rendered at — so the
    // first stroke-scaled render lines up with what the user sees on
    // load.
    const canonical = canonicalizeLegacyDrawing(annotation.content.strokes, t.rotation);
    return {
        x: canonical.anchor.x,
        y: canonical.anchor.y,
        width: canonical.transform.width,
        height: canonical.transform.height,
        rotation: canonical.transform.rotation,
        naturalWidth: canonical.transform.naturalWidth ?? canonical.transform.width,
        naturalHeight: canonical.transform.naturalHeight ?? canonical.transform.height,
        strokes: canonical.strokes,
    };
}
/**
 * True when an annotation is stored in the canonical (post-transform-
 * feature) shape. Used by gesture handlers to decide whether the
 * first PATCH for this drawing needs to also rewrite strokes
 * (legacy → canonical) or can ship as a pure transform patch.
 */
export function isCanonicalDrawing(annotation) {
    return annotation.anchor?.type === "panel_point";
}
